/**
 * CompanyPaymentBulkAddPage — iter-218o (Feb 2026)
 *
 * Route: /finance/asb606/payments/company/bulk-add
 * Sibling to CompanyPaymentAddPage — this one accepts up to 50 receipts
 * in a single admin session:
 *   1) Pick ONE entity (same list as the single-add page).
 *   2) Upload N receipt files (≤ 50).
 *   3) OCR each file in parallel (max 5 concurrent) — amount, date,
 *      currency, reference are auto-filled per row.
 *   4) Admin reviews each row inline (edit amount / date / invoice #).
 *   5) Submit — one POST /api/finance/service-payments per ready row.
 * The final table shows per-row status: pending → ocr → ready → done /
 * error, plus a summary banner (created N / failed M).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, Save, Upload, X, CheckCircle2, AlertTriangle, Building2,
  FilePlus2, Trash2, Layers, RefreshCw,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { floorAED } from '../../utils/money';

const BACKEND_URL     = process.env.REACT_APP_BACKEND_URL;
const MAX_RECEIPT_MB  = 5;
const MAX_FILES       = 50;
const OCR_CONCURRENCY = 5;

// Row lifecycle: pending → ocr → ready → done | error
const STATUS_META = {
  pending: { color: 'bg-white/10  text-white/60 border-white/15',       ar: 'بانتظار الفحص',    en: 'Pending'   },
  ocr:     { color: 'bg-blue-500/15 text-blue-200 border-blue-500/40',  ar: 'يُفحص…',           en: 'OCR…'      },
  ready:   { color: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40', ar: 'جاهز للحفظ', en: 'Ready'    },
  saving:  { color: 'bg-amber-500/15 text-amber-200 border-amber-500/40', ar: 'يُحفظ…',          en: 'Saving…'   },
  done:    { color: 'bg-emerald-500/20 text-emerald-100 border-emerald-500/60', ar: 'تم الحفظ ✓', en: 'Saved ✓'  },
  error:   { color: 'bg-rose-500/15 text-rose-200 border-rose-500/40', ar: 'خطأ',              en: 'Error'     },
};

const newRow = (file, dataUrl) => ({
  id:         `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  file, dataUrl,
  status:     'pending',
  is_receipt: null,
  // Editable
  amount:     '',     // in `currency` (AED → saved as-is; entity currency → × rate)
  currency:   'AED',  // row currency: 'AED' or the entity's foreign currency
  input_mode: 'aed',
  date:       new Date().toISOString().slice(0, 10),
  reference:  '',
  // Meta
  currency_source: 'entity',  // 'receipt' when the AI read an explicit currency on the receipt
  ocr_currency: '',
  error_msg:  '',
});

// The receipt explicitly shows dirhams (AED / Dhs / د.إ / درهم) next to the total.
const AED_RX = /(AED|DHS?\b|DH\b|د\.?\s?إ|درهم)/i;
const receiptSaysAed = (d) =>
  String(d?.currency || '').toUpperCase() === 'AED'
  && (d?.currency_confidence === 'high' || AED_RX.test(String(d?.currency_evidence || '')));

export default function CompanyPaymentBulkAddPage() {
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEntryMode = (searchParams.get('mode') || '').toLowerCase() === 'entry';
  const listPath = isEntryMode ? '/finance/asb606/payments/company?mode=entry' : '/finance/asb606/payments/company';
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const authHeaders = useCallback(() => getAuthHeaders(), [getAuthHeaders]);

  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [rows,     setRows]     = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const fileInputRef = useRef(null);

  // iter-218p — CRITICAL FIX: `runOcrForRows` used to read `rows` from a stale
  // React closure, so newly-appended rows (batches of 50 uploaded at once)
  // could not be found by the workers and were silently skipped — the admin
  // saw "8 of 50 processed" symptom. We now mirror the latest rows into a ref
  // so workers always see the freshest list.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const entity = useMemo(
    () => entities.find(x => x.id === entityId) || null,
    [entities, entityId],
  );
  const entityCurrency = (entity?.currency || 'AED').toUpperCase();
  const entityRate     = parseFloat(entity?.exchange_rate) || 1;
  const isForeign      = entityCurrency !== 'AED';

  // ── Load entities ────────────────────────────────────────────────
  const loadRefs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/finance/payment-services`, { headers: authHeaders() });
      setEntities(r?.data?.data || []);
    } catch (err) {
      toast.error(t('فشل تحميل الجهات', 'Failed to load entities'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]); // eslint-disable-line

  useEffect(() => { window.scrollTo(0, 0); loadRefs(); }, [loadRefs]);

  // ── File upload ──────────────────────────────────────────────────
  const handleFilesChosen = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    if (!entityId) {
      toast.error(t('اختر الجهة أولاً', 'Pick the entity first'));
      return;
    }
    // Respect the 50-file cap across the whole session.
    const remaining = MAX_FILES - rows.length;
    if (remaining <= 0) {
      toast.error(t(`الحد الأقصى ${MAX_FILES} إيصال`, `Max ${MAX_FILES} receipts per batch`));
      return;
    }
    const toAdd = list.slice(0, remaining);
    if (list.length > remaining) {
      toast.warning(t(`تم قبول ${toAdd.length} فقط — الحد الأقصى ${MAX_FILES}`,
                       `Only ${toAdd.length} accepted — max ${MAX_FILES}`));
    }
    // Read all files to data-URLs sequentially (small enough).
    const readFile = (f) => new Promise((resolve, reject) => {
      if (f.size > MAX_RECEIPT_MB * 1024 * 1024) {
        return reject(new Error(t(`${f.name} أكبر من ${MAX_RECEIPT_MB}MB`,
                                   `${f.name} exceeds ${MAX_RECEIPT_MB}MB`)));
      }
      const r = new FileReader();
      r.onload = (ev) => resolve(ev.target.result);
      r.onerror = () => reject(new Error(t('فشل قراءة الملف', 'Failed to read file')));
      r.readAsDataURL(f);
    });
    const built = [];
    for (const f of toAdd) {
      try {
        const dataUrl = await readFile(f);
        built.push(newRow(f, dataUrl));
      } catch (err) {
        toast.error(err.message);
      }
    }
    if (built.length === 0) return;
    setRows(prev => [...prev, ...built]);
    // iter-218p — Wait a tick so `rowsRef` catches the new rows before
    // workers try to look them up (React 18 batching + effect commit).
    setTimeout(() => runOcrForRows(built.map(b => b.id)), 60);
  };

  const setRowById = (id, patch) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...(typeof patch === 'function' ? patch(r) : patch) } : r));
  };

  const removeRow = (id) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  // ── OCR (max 5 concurrent, with 1-shot auto-retry on network/429/5xx) ──
  const ocrOne = async (row, isRetry = false) => {
    setRowById(row.id, { status: 'ocr', error_msg: '' });
    try {
      const b64 = row.dataUrl.split(',', 2)[1];
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/assigned-tasks/receipts/extract`,
        { file_base64: b64, mime: row.file.type },
        { headers: authHeaders(), timeout: 60_000 },
      );
      const data = r.data?.data || {};
      const amount = Number(data.amount) || 0;
      const patch = {
        status:     data.is_receipt ? 'ready' : 'error',
        is_receipt: data.is_receipt,
        error_msg:  data.is_receipt ? '' : t('الملف ليس إيصالاً — يمكنك المحاولة مجدداً', 'Not recognised as a receipt — you can retry'),
      };
      if (data.date && /^\d{4}-\d{2}-\d{2}$/.test(data.date)) patch.date = data.date;
      if (data.reference) patch.reference = String(data.reference);
      if (amount > 0) {
        patch.amount = String(amount);
        // Currency rule (Jun 2026):
        //   • AED entity → always AED.
        //   • Foreign entity + receipt explicitly in dirhams → AED, NO conversion.
        //   • Foreign entity + receipt in the entity currency / unclear → entity currency × rate.
        const ocrCurr = String(data.currency || '').toUpperCase();
        patch.ocr_currency = ocrCurr;
        if (!isForeign) {
          patch.currency = 'AED'; patch.input_mode = 'aed'; patch.currency_source = 'entity';
        } else if (receiptSaysAed(data)) {
          patch.currency = 'AED'; patch.input_mode = 'aed'; patch.currency_source = 'receipt';
        } else {
          patch.currency = entityCurrency; patch.input_mode = 'foreign';
          patch.currency_source = ocrCurr === entityCurrency ? 'receipt' : 'entity';
        }
      }
      setRowById(row.id, patch);
    } catch (err) {
      const status = err?.response?.status;
      // 424 (Failed Dependency) is the code the backend returns when Gemini
      // itself flakes (quota, transient network, content-filter etc.) — that's
      // exactly the kind of hiccup a one-shot retry usually fixes. We also
      // retry generic 5xx and network aborts.
      const isRetryable = !status || status === 424 || status === 429 || status >= 500 || err?.code === 'ECONNABORTED';
      if (!isRetry && isRetryable) {
        // Short back-off then re-attempt once — many Gemini rate-limit hiccups
        // resolve within a second.
        await new Promise((res) => setTimeout(res, 1500));
        return ocrOne(row, true);
      }
      setRowById(row.id, {
        status: 'error',
        error_msg: err?.response?.data?.detail || t('فشل الفحص — اضغط "إعادة قراءة"', 'OCR failed — press "Retry"'),
      });
    }
  };

  const runOcrForRows = async (idList) => {
    if (!idList || idList.length === 0) return;
    const queue = [...idList];
    setOcrRunning(true);
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        if (!id) continue;
        // iter-218p — always look at the LATEST rows via ref (not stale closure).
        const currentRow = rowsRef.current.find(r => r.id === id);
        if (!currentRow) continue;
        // Only OCR rows that are actually awaiting a fetch.
        if (currentRow.status !== 'pending' && currentRow.status !== 'ocr') continue;
        await ocrOne(currentRow);
      }
    };
    await Promise.all(Array.from({ length: OCR_CONCURRENCY }, worker));
    setOcrRunning(false);
  };

  // iter-218p — Retry helpers
  const retryRow = (id) => {
    const row = rowsRef.current.find(r => r.id === id);
    if (!row) return;
    // Any row that isn't already saved / mid-save can be re-read.
    if (row.status === 'saving' || row.status === 'done') return;
    setRowById(id, { status: 'pending', error_msg: '' });
    // Kick a single-row OCR run.
    runOcrForRows([id]);
  };

  const retryAllUnread = () => {
    // "Unread" = anything that isn't already ready/saving/done. We include
    // `error`, `pending`, and stuck `ocr` rows (in case a previous run was
    // interrupted). `ready` rows are already successfully read.
    const targets = rowsRef.current
      .filter(r => r.status === 'error' || r.status === 'pending' || r.status === 'ocr')
      .map(r => r.id);
    if (targets.length === 0) {
      toast.info(t('لا توجد إيصالات بحاجة لإعادة قراءة', 'No receipts need re-reading'));
      return;
    }
    // Reset them all to pending so the worker will pick them up.
    targets.forEach(id => setRowById(id, { status: 'pending', error_msg: '' }));
    // Small delay so React commits the state before workers read the ref.
    setTimeout(() => runOcrForRows(targets), 60);
    toast.info(t(`جاري إعادة قراءة ${targets.length} إيصالاً…`, `Re-reading ${targets.length} receipt${targets.length === 1 ? '' : 's'}…`));
  };

  // ── Submit all ready rows ───────────────────────────────────────
  const readyRows = rows.filter(r => r.status === 'ready');
  const canSubmit = !!entity && readyRows.length > 0 && !submitting && !ocrRunning;

  const finalAedFor = (row) => {
    const amt = parseFloat(row.amount) || 0;
    // Row currency decides: AED rows are stored as-is (no conversion); rows in the
    // entity's foreign currency are multiplied by the entity's exchange rate.
    if (isForeign && row.currency !== 'AED') return floorAED(amt * entityRate);
    return floorAED(amt);
  };

  const toggleRowCurrency = (row) => {
    if (!isForeign || ['saving', 'done', 'ocr', 'pending'].includes(row.status)) return;
    const toAed = row.currency !== 'AED';
    setRowById(row.id, { currency: toAed ? 'AED' : entityCurrency, input_mode: toAed ? 'aed' : 'foreign', currency_source: 'manual' });
  };

  const submitAll = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    let created = 0;
    let failed = 0;
    for (const row of readyRows) {
      setRowById(row.id, { status: 'saving' });
      const aed = finalAedFor(row);
      if (aed <= 0) {
        setRowById(row.id, { status: 'error', error_msg: t('المبلغ صفر أو غير صالح', 'Amount is zero or invalid') });
        failed++;
        continue;
      }
      const rowForeign = isForeign && row.currency !== 'AED';
      try {
        await axios.post(`${BACKEND_URL}/api/finance/service-payments`, {
          service_type:      entity.id,
          amount:            rowForeign ? parseFloat(row.amount) || 0 : aed,
          amount_aed:        aed,
          currency:          rowForeign ? entityCurrency : 'AED',
          rate_used:         rowForeign ? entityRate : 1,
          payment_date:      row.date,
          invoice_number:    row.reference,
          invoice_file_url:  row.dataUrl,
          ocr_verified:      true,
          ocr_currency:      row.ocr_currency || null,
          currency_source:   row.currency_source,
          notes:             t('إضافة مجمّعة (Bulk)', 'Bulk-add batch'),
        }, { headers: authHeaders() });
        setRowById(row.id, { status: 'done' });
        created++;
      } catch (err) {
        setRowById(row.id, {
          status: 'error',
          error_msg: err?.response?.data?.detail || t('فشل الحفظ', 'Save failed'),
        });
        failed++;
      }
    }
    setSubmitting(false);
    if (created > 0) {
      toast.success(t(`تم حفظ ${created} دفعة بنجاح${failed ? ` — فشل ${failed}` : ''}`,
                       `Saved ${created} payment${created === 1 ? '' : 's'}${failed ? ` — ${failed} failed` : ''}`));
    } else if (failed > 0) {
      toast.error(t(`فشل حفظ جميع الدفعات (${failed})`, `All ${failed} payments failed`));
    }
  };

  const clearAll = () => {
    if (rows.length && !window.confirm(t('حذف جميع الإيصالات؟', 'Remove all receipts?'))) return;
    setRows([]);
  };

  // ── Render ──────────────────────────────────────────────────────
  const doneCount    = rows.filter(r => r.status === 'done').length;
  const errorCount   = rows.filter(r => r.status === 'error').length;
  const readyCount   = readyRows.length;
  const ocrPending   = rows.filter(r => r.status === 'pending' || r.status === 'ocr').length;

  return (
    <DashboardLayout
      title="إضافة دفعات مجمّعة"
      titleEn="Bulk Add Payments"
      subtitle="اختر الجهة، ارفع حتى 50 إيصالاً، وسيقوم الذكاء الاصطناعي بفصل كل واحد كدفعة منفصلة"
      subtitleEn="Pick an entity, upload up to 50 receipts, and the AI will split each one into its own payment"
      screenCode="ASB-FIN-PAY-CO-BULK"
      backPath={listPath}
      onBack={() => navigate(listPath)}
      breadcrumb={[
        { labelAr: 'المالية',           labelEn: 'Finance',            path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)',  labelEn: 'ASB606 (Finance)',   path: '/finance/asb606' },
        isEntryMode
          ? { labelAr: 'الإدراج المالي للشركة', labelEn: 'Financial Entry', path: '/finance/asb606/payments/entry' }
          : { labelAr: 'المدفوعات',     labelEn: 'Payments',           path: '/finance/asb606/payments' },
        { labelAr: 'مدفوعات الشركة',     labelEn: 'Company Payments',   path: listPath },
        { labelAr: 'إضافة دفعات',        labelEn: 'Bulk Add' },
      ]}
    >
      <main className="relative z-10 py-6 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" dir={isAr ? 'rtl' : 'ltr'} data-testid="company-payment-bulk-page">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-white/50 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('جاري التحميل…', 'Loading…')}
          </div>
        ) : (
          <>
            {/* Step 1 — Entity picker */}
            <section className="rounded-2xl bg-white/[0.02] border border-white/10 p-5 mb-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-500/40 flex items-center justify-center text-blue-300 text-sm font-bold">1</div>
                <h2 className="text-white font-bold">{t('اختر الجهة', 'Pick the entity')} *</h2>
              </div>
              <select
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                disabled={rows.length > 0}
                className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-11 px-3 text-sm focus:outline-none focus:border-[#d4af37]/60 disabled:opacity-50"
                data-testid="bulk-entity-select"
                dir={isAr ? 'rtl' : 'ltr'}
              >
                <option value="">{t('— اختر الجهة —', '— Pick an entity —')}</option>
                {entities.map(e => (
                  <option key={e.id} value={e.id}>
                    {isAr ? (e.name_ar || e.name) : (e.name_en || e.name)}
                    {e.currency && e.currency !== 'AED' ? `  (${e.currency} → AED @ ${e.exchange_rate || 1})` : ''}
                  </option>
                ))}
              </select>
              {rows.length > 0 && (
                <div className="mt-2 text-[11px] text-amber-300">
                  {t('لا يمكن تغيير الجهة بعد رفع الإيصالات — احذفها أولاً.',
                     'Can\'t switch entity after receipts are uploaded — remove them first.')}
                </div>
              )}
              {isForeign && (
                <div className="mt-2 text-[11px] text-blue-200">
                  {t(`سعر الصرف المستخدم: 1 ${entityCurrency} = ${entityRate} AED — الحفظ النهائي بالدرهم.`,
                     `Rate used: 1 ${entityCurrency} = ${entityRate} AED — final stored value is AED.`)}
                </div>
              )}
            </section>

            {/* Step 2 — Uploader */}
            <section className="rounded-2xl bg-white/[0.02] border border-white/10 p-5 mb-5">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-sm font-bold">2</div>
                  <h2 className="text-white font-bold">{t(`ارفع الإيصالات (${rows.length}/${MAX_FILES})`, `Upload receipts (${rows.length}/${MAX_FILES})`)}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => { handleFilesChosen(e.target.files); e.target.value = ''; }}
                    data-testid="bulk-file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!entityId || rows.length >= MAX_FILES}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bulk-upload-btn"
                  >
                    <FilePlus2 className="w-4 h-4 me-1.5" />
                    {t('اختر الملفات', 'Choose files')}
                  </Button>
                  {rows.length > 0 && (
                    <>
                      {/* iter-218p — Retry-all button for any pending/error/ocr rows. */}
                      <Button
                        onClick={retryAllUnread}
                        disabled={ocrRunning || submitting || (ocrPending + errorCount === 0)}
                        variant="ghost"
                        className="text-blue-200 hover:bg-blue-500/10 disabled:opacity-40"
                        data-testid="bulk-retry-all-btn"
                      >
                        <RefreshCw className={`w-4 h-4 me-1.5 ${ocrRunning ? 'animate-spin' : ''}`} />
                        {t(`إعادة قراءة الغير مقروء${(ocrPending + errorCount) > 0 ? ` (${ocrPending + errorCount})` : ''}`,
                           `Retry unread${(ocrPending + errorCount) > 0 ? ` (${ocrPending + errorCount})` : ''}`)}
                      </Button>
                      <Button variant="ghost" onClick={clearAll}
                              className="text-rose-300 hover:bg-rose-500/10"
                              data-testid="bulk-clear-btn">
                        <Trash2 className="w-4 h-4 me-1.5" />
                        {t('مسح الكل', 'Clear all')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-white/50">
                {t(`أقصى حجم لكل ملف: ${MAX_RECEIPT_MB}MB — الأنواع المقبولة: صور و PDF. سيتم فحص كل ملف بالذكاء الاصطناعي تلقائياً.`,
                   `Max size per file: ${MAX_RECEIPT_MB}MB — accepted: images + PDF. Each file will be AI-verified automatically.`)}
              </div>
            </section>

            {/* Rows table */}
            {rows.length > 0 && (
              <section className="rounded-2xl bg-white/[0.02] border border-white/10 overflow-hidden mb-5">
                {/* iter-218q — Prominent FX-rate banner: shown ABOVE the table
                     when the entity is in a foreign currency, so admins can
                     spot at a glance the exact rate being applied to every row. */}
                {isForeign && (
                  <div className="px-3 py-2 border-b border-blue-500/20 bg-blue-500/10 flex items-center gap-2 text-[12px] text-blue-100" data-testid="bulk-fx-banner">
                    <RefreshCw className="w-3.5 h-3.5 text-blue-300" />
                    <span>
                      {t(
                        `سعر الصرف المطبَّق: 1 ${entityCurrency} = ${entityRate} د.إ — الإيصالات بعملة ${entityCurrency} تُضرب في هذا السعر، أما الإيصالات التي يكتشفها الذكاء الاصطناعي بالدرهم فتُحفظ كما هي بدون تحويل (يمكنك تبديل عملة أي صف يدوياً)${entityRate === 1 ? '  ⚠️ السعر = 1 — تأكد من إعداد الجهة!' : ''}`,
                        `FX rate applied: 1 ${entityCurrency} = ${entityRate} AED — ${entityCurrency} receipts are multiplied by this rate; receipts the AI detects in dirhams are saved as-is with no conversion (you can flip any row's currency manually)${entityRate === 1 ? '  ⚠️ Rate is 1 — check the entity setup!' : ''}`
                      )}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 border-b border-white/5 text-[11px]">
                  <StatChip label={t('جاهز للحفظ', 'Ready')}       value={readyCount}   tone="emerald" />
                  <StatChip label={t('يُفحص / معلق', 'OCR pending')} value={ocrPending} tone="blue" />
                  <StatChip label={t('محفوظة', 'Saved')}            value={doneCount}    tone="emerald" />
                  <StatChip label={t('أخطاء', 'Errors')}            value={errorCount}   tone="rose" />
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.03] border-b border-white/10 text-white/50 text-[11px] uppercase">
                      <tr>
                        <th className="text-start px-3 py-2 w-16">{t('الحالة', 'Status')}</th>
                        <th className="text-start px-3 py-2">{t('الملف', 'File')}</th>
                        <th className="text-start px-3 py-2 w-32">{t('التاريخ', 'Date')}</th>
                        <th className="text-start px-3 py-2 w-36">{t('رقم الحوالة', 'Reference')}</th>
                        <th className="text-end px-3 py-2 w-44">
                          {isForeign ? t('المبلغ + العملة', 'Amount + currency') : t('المبلغ (د.إ)', 'Amount (AED)')}
                        </th>
                        <th className="text-end px-3 py-2 w-28">{t('د.إ نهائي', 'AED final')}</th>
                        <th className="w-20 px-3 py-2 text-center">{t('إجراءات', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {rows.map((r) => {
                        const meta = STATUS_META[r.status] || STATUS_META.pending;
                        return (
                          <tr key={r.id} data-testid={`bulk-row-${r.id}`}>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border ${meta.color}`}>
                                {isAr ? meta.ar : meta.en}
                              </span>
                              {r.error_msg && (
                                <div className="text-[10px] text-rose-300 mt-1 truncate max-w-[100px]" title={r.error_msg}>
                                  {r.error_msg}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <a href={r.dataUrl} target="_blank" rel="noreferrer"
                                 className="text-white/80 hover:text-white text-xs truncate block max-w-[160px]"
                                 title={r.file.name}>
                                {r.file.name}
                              </a>
                              <div className="text-[10px] text-white/40">
                                {(r.file.size / 1024).toFixed(0)}KB
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <Input type="date" value={r.date}
                                     onChange={(e) => setRowById(r.id, { date: e.target.value })}
                                     disabled={r.status === 'saving' || r.status === 'done'}
                                     className="bg-[#050505] border-white/10 text-white h-8 text-xs" />
                            </td>
                            <td className="px-3 py-2">
                              <Input value={r.reference}
                                     onChange={(e) => setRowById(r.id, { reference: e.target.value })}
                                     disabled={r.status === 'saving' || r.status === 'done'}
                                     placeholder={t('اختياري', 'Optional')}
                                     className="bg-[#050505] border-white/10 text-white h-8 text-xs" dir="ltr" />
                            </td>
                            <td className="px-3 py-2 text-end">
                              <div className="flex items-center justify-end gap-1.5">
                                {isForeign && (
                                  <button
                                    type="button"
                                    onClick={() => toggleRowCurrency(r)}
                                    disabled={['saving', 'done', 'ocr', 'pending'].includes(r.status)}
                                    title={r.currency === 'AED'
                                      ? t('الإيصال بالدرهم — يُحفظ بدون تحويل (اضغط للتبديل)', 'Receipt in AED — saved without conversion (click to switch)')
                                      : t(`بعملة ${entityCurrency} — يُحوَّل إلى الدرهم (اضغط للتبديل)`, `In ${entityCurrency} — converted to AED (click to switch)`)}
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold border shrink-0 disabled:opacity-50 ${
                                      r.currency === 'AED'
                                        ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
                                        : 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                                    }`}
                                    data-testid={`bulk-currency-${r.id}`}
                                  >
                                    {r.currency}
                                  </button>
                                )}
                                <Input type="number" step="0.01" min="0" value={r.amount}
                                       onChange={(e) => setRowById(r.id, {
                                         amount: e.target.value,
                                         // Manual edit → mark ready if it was error but has amount
                                         status: (r.is_receipt !== false && parseFloat(e.target.value) > 0 && r.status === 'error')
                                                   ? 'ready' : r.status,
                                       })}
                                       disabled={r.status === 'saving' || r.status === 'done'}
                                       className="bg-[#050505] border-white/10 text-white h-8 text-xs text-end w-28" dir="ltr" />
                              </div>
                              {isForeign && r.currency === 'AED' && r.currency_source === 'receipt' && (
                                <div className="text-[10px] text-emerald-300/80 mt-1" data-testid={`bulk-aed-note-${r.id}`}>
                                  {t('اكتُشف بالدرهم — بدون تحويل', 'Detected in AED — no conversion')}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-end text-emerald-200 font-medium">
                              {finalAedFor(r).toFixed(2)}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-center gap-1">
                                {/* iter-218p — per-row Retry button. Visible while the row
                                     isn't already saved/mid-save. Disabled while OCR is
                                     mid-flight for this row. */}
                                {(r.status !== 'saving' && r.status !== 'done') && (
                                  <button
                                    onClick={() => retryRow(r.id)}
                                    disabled={r.status === 'ocr'}
                                    title={t('إعادة قراءة', 'Re-read')}
                                    className="p-1 rounded hover:bg-blue-500/15 text-blue-300/80 disabled:opacity-40"
                                    data-testid={`bulk-retry-${r.id}`}
                                  >
                                    <RefreshCw className={`w-4 h-4 ${r.status === 'ocr' ? 'animate-spin' : ''}`} />
                                  </button>
                                )}
                                {(r.status !== 'saving' && r.status !== 'done') && (
                                  <button onClick={() => removeRow(r.id)}
                                          title={t('حذف', 'Remove')}
                                          className="p-1 rounded hover:bg-rose-500/15 text-rose-400/70"
                                          data-testid={`bulk-remove-${r.id}`}>
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                                {r.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-300" />}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Submit */}
            {rows.length > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-white/60">
                  {ocrRunning && <span className="inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('جاري فحص الإيصالات…', 'Verifying receipts…')}</span>}
                  {!ocrRunning && readyCount > 0 && (
                    <span>{t(`جاهز: ${readyCount} من ${rows.length - doneCount}`, `Ready: ${readyCount} of ${rows.length - doneCount}`)}</span>
                  )}
                </div>
                <Button
                  onClick={submitAll}
                  disabled={!canSubmit}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-6 disabled:opacity-40"
                  data-testid="bulk-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 me-1.5 animate-spin" /> : <Layers className="w-4 h-4 me-1.5" />}
                  {t(`حفظ ${readyCount} دفعة`, `Save ${readyCount} payment${readyCount === 1 ? '' : 's'}`)}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </DashboardLayout>
  );
}

// Small KPI chip used above the table.
function StatChip({ label, value, tone }) {
  const tones = {
    emerald: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/30',
    blue:    'bg-blue-500/10    text-blue-200    border-blue-500/30',
    rose:    'bg-rose-500/10    text-rose-200    border-rose-500/30',
    default: 'bg-white/5        text-white/70    border-white/10',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 flex items-center justify-between ${tones[tone] || tones.default}`}>
      <span>{label}</span>
      <span className="text-base font-bold">{value}</span>
    </div>
  );
}
